import { useCallback, useEffect, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { auth, db } from "../../firebase/firebase.js";
import { TEACHER_STATUS } from "../../utils/teacherStatus.js";
import styles from "./Admin.module.css";

function formatWhen(value) {
  if (!value) return "—";
  const date = value?.toDate?.() || new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-GB");
}

function displayName(row) {
  const name = [row.firstName, row.lastName].filter(Boolean).join(" ").trim();
  return name || row.email || row.id;
}

export default function AdminTeachers() {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState(TEACHER_STATUS.PENDING);

  const loadTeachers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const q = query(
        collection(db, "teachers"),
        where("status", "==", filter)
      );
      const snap = await getDocs(q);
      const rows = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      rows.sort((a, b) => {
        const aTime = Date.parse(a.createdAt) || a.createdAt?.toMillis?.() || 0;
        const bTime = Date.parse(b.createdAt) || b.createdAt?.toMillis?.() || 0;
        return bTime - aTime;
      });
      setTeachers(rows);
    } catch (err) {
      setError(err.message || "Failed to load tutor applications");
      setTeachers([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadTeachers();
  }, [loadTeachers]);

  const moderate = async (row, status) => {
    setBusyId(row.id);
    setError("");
    try {
      await updateDoc(doc(db, "teachers", row.id), {
        status,
        reviewedAt: serverTimestamp(),
        reviewedBy: auth.currentUser?.uid || null,
        reviewedByEmail: auth.currentUser?.email || null,
      });
      setTeachers((prev) => prev.filter((t) => t.id !== row.id));
    } catch (err) {
      setError(err.message || "Failed to update tutor application");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <h1 className={styles.title}>Tutor applications</h1>
      <p className={styles.lead}>
        Approve new tutor registrations before they can appear in the directory
        or use the tutor app. Existing tutors without an application status stay
        active.
      </p>

      <div className={styles.panel}>
        <div className={styles.row}>
          <button
            type="button"
            className={
              filter === TEACHER_STATUS.PENDING ? styles.buttonOk : styles.button
            }
            onClick={() => setFilter(TEACHER_STATUS.PENDING)}
          >
            Pending
          </button>
          <button
            type="button"
            className={
              filter === TEACHER_STATUS.REJECTED
                ? styles.buttonDanger
                : styles.button
            }
            onClick={() => setFilter(TEACHER_STATUS.REJECTED)}
          >
            Rejected
          </button>
          <button type="button" className={styles.button} onClick={loadTeachers}>
            Refresh
          </button>
        </div>
        {error && <p className={styles.error}>{error}</p>}
        {loading ? (
          <p className={styles.muted}>Loading…</p>
        ) : teachers.length === 0 ? (
          <p className={styles.muted}>
            {filter === TEACHER_STATUS.REJECTED
              ? "No rejected applications."
              : "No pending tutor applications."}
          </p>
        ) : (
          teachers.map((row) => (
            <div key={row.id} className={styles.card}>
              <strong>{displayName(row)}</strong>
              <div className={styles.meta}>
                <span>{row.email || "no email"}</span>
                <span>ID: {row.id}</span>
                <span>Applied: {formatWhen(row.createdAt)}</span>
              </div>
              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.buttonOk}
                  disabled={busyId === row.id}
                  onClick={() => moderate(row, TEACHER_STATUS.APPROVED)}
                >
                  Approve
                </button>
                {filter !== TEACHER_STATUS.REJECTED && (
                  <button
                    type="button"
                    className={styles.buttonDanger}
                    disabled={busyId === row.id}
                    onClick={() => moderate(row, TEACHER_STATUS.REJECTED)}
                  >
                    Reject
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
